import { envVarsProvider } from '@/utils/envVars/envVars';

/** URL param names accepted by the /logs page for filtering. */
export type LogsFilterField =
  | 'agentId'
  | 'agentName'
  | 'constellationName'
  | 'constellationVersion'
  | 'clientId'
  | 'correlationId'
  | 'requestId'
  | 'sessionIdFilter'
  | 'modelId';

export type LogsFilterLogType = 'modelLog' | 'actionLog' | 'requestResponseLog' | 'trace';

/** Fields rendered inline in the /logs toolbar; the rest live in the advanced
 *  filters panel and need `filtersOpen=1` so the user can see what's applied. */
const INLINE_FIELDS = new Set<LogsFilterField>( [ 'agentId', 'correlationId', 'requestId' ] );

function getBasename(): string {
  const raw = envVarsProvider.get( 'STUDIO_BASE_PATH' ) || '';
  if ( !raw || raw === '/' ) return '';
  const withLeading = raw.startsWith( '/' ) ? raw : `/${ raw }`;
  return withLeading.endsWith( '/' ) ? withLeading.slice( 0, -1 ) : withLeading;
}

/** Build a shareable /logs URL filtered by a single field. */
export function buildLogsFilterUrl(
  field: LogsFilterField,
  value: string,
  logType: LogsFilterLogType
): string {
  const params = new URLSearchParams();
  params.set( 'logType', logType );
  params.set( field, value );
  if ( !INLINE_FIELDS.has( field ) ) params.set( 'filtersOpen', '1' );
  return `${ getBasename() }/logs?${ params.toString() }`;
}

/** Build a playground URL that tells the playground to fetch the request log
 *  by request_id, extract messages/context/users from it, and resubmit. Keeps
 *  the URL short (no payload encoding) so we don't hit 431 / header-size limits. */
export function buildReplayPlaygroundUrl( {
  agentId,
  requestId
}: {
  agentId: string;
  requestId: string;
} ): string {
  const params = new URLSearchParams( {
    an: agentId,
    t: 'aidp',
    replayRequestId: requestId,
    autoSend: '1'
  } );
  return `${ getBasename() }/agents/playground?${ params.toString() }`;
}
